import React, { useEffect, useRef } from 'react';
import { useSubscriptionUsage, UsageState } from '../hooks/useSubscriptionUsage';

interface UsageIndicatorProps {
  showDetailed?: boolean;
  compact?: boolean;
  className?: string;
  // Optional props to bypass the hook
  usageData?: Partial<UsageState>; 
}

/**
 * Component that displays subscription usage information to the user
 */
const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  showDetailed = false,
  compact = false,
  className = '',
  usageData: usageDataFromProps // Rename prop for clarity
}) => {
  // Determine if we should use the hook or props
  const shouldUseHook = !usageDataFromProps;
  const hookData = useSubscriptionUsage();

  // Combine hook data and prop data
  const combinedUsageData = shouldUseHook 
    ? hookData 
    : { ...hookData, ...usageDataFromProps }; // Merge props over hook defaults if props provided

  const {
    isLoading,
    error,
    usedCount = 0, // Default values if not provided
    limit = 0,
    remaining = 0,
    percentage = 0,
    canPerformAnalysis = false,
    hasActiveSubscription = false,
    planType = 'free',
    usageLevel = 'low',
    fetchUsage,
    isPremium = false,
    isStandard = false,
    isFree = true
  } = combinedUsageData;

  // Ref for auto-refresh (only used if shouldUseHook is true)
  const autoRefreshEnabledRef = useRef(true);

  // Only set up periodic refresh if using the hook (i.e., not compact sidebar mode with props)
  useEffect(() => {
    if (!shouldUseHook) return; // Don't auto-refresh if props are provided

    // Set up automatic refresh interval
    const refreshInterval = compact ? 60000 : 120000; // 1 minute for compact, 2 minutes for full
    
    const controlledFetch = () => {
      if (autoRefreshEnabledRef.current) {
        console.log(`Auto-refreshing usage data (hook mode - ${compact ? 'sidebar' : 'full'})`);
        fetchUsage();
      }
    };
    
    const intervalId = setInterval(controlledFetch, refreshInterval);
    
    const handleVisibilityChange = () => {
      autoRefreshEnabledRef.current = document.visibilityState === 'visible';
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [compact, fetchUsage, shouldUseHook]);

  // Manual refresh handler (only relevant if using hook)
  const handleManualRefresh = () => {
    if (!shouldUseHook) return; // No manual refresh if using props
    autoRefreshEnabledRef.current = false;
    fetchUsage();
    setTimeout(() => {
      autoRefreshEnabledRef.current = true;
    }, 5000); 
  };

  // Loading state only relevant when using the hook
  if (shouldUseHook && isLoading) {
    return (
      <div className={`bg-gray-100 rounded-md p-2 animate-pulse ${className}`}>
        {compact ? (
          <div className="flex items-center justify-center">
            <span className="h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></span>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Loading...</p>
        )}
      </div>
    );
  }

  // Error state only relevant when using the hook
  if (shouldUseHook && error && !compact) {
    return (
      <div className={`bg-red-50 text-red-600 p-3 rounded-md ${className}`}>
        <p className="text-sm font-medium">
          {error}
          <button 
            className="ml-2 text-red-700 underline hover:text-red-800" 
            onClick={handleManualRefresh}
          >
            Retry
          </button>
        </p>
      </div>
    );
  }

  // Compact version (now uses combinedUsageData)
  if (compact) {
    // Calculate derived values needed for compact view if not provided directly
    const displayPercentage = percentage;
    const displayUsageLevel = usageLevel;
    const displayCanPerformAnalysis = canPerformAnalysis;
    const displayHasActiveSubscription = hasActiveSubscription;

    return (
      <div 
        className={`relative ${className}`} 
        title={`${usedCount}/${limit} analyses used (${remaining} remaining)`}
      >
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${
              displayUsageLevel === 'critical' ? 'bg-red-500' :
              displayUsageLevel === 'warning' ? 'bg-amber-500' :
              displayUsageLevel === 'moderate' ? 'bg-blue-500' : 'bg-green-500'
            }`}
            style={{ width: `${displayPercentage}%` }}
          ></div>
        </div>
        {!displayCanPerformAnalysis && !displayHasActiveSubscription && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full" title="Subscription inactive"></span>
        )}
        {!displayCanPerformAnalysis && displayHasActiveSubscription && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-amber-500 rounded-full" title="Usage limit reached"></span>
        )}
      </div>
    );
  }

  // Calculate renewal date for display (first of next month) - Only for detailed view?
  const today = new Date();
  const renewalDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const formattedRenewalDate = renewalDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric'
  });
  const daysUntilRenewal = Math.ceil((renewalDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  // Enhanced full detailed version (uses combinedUsageData)
  return (
    <div className={`bg-white rounded-lg border ${
      usageLevel === 'critical' ? 'border-red-200' :
      usageLevel === 'warning' ? 'border-amber-200' :
      'border-gray-200'
    } ${className}`}>
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h4 className="font-medium text-gray-800 flex items-center">
          <span className={`mr-2 ${
            isPremium ? 'text-purple-600' : 
            isStandard ? 'text-blue-600' : 
            'text-gray-600'
          }`}>
            {isPremium ? '✨' : isStandard ? '⭐' : '•'}
          </span>
          {isPremium ? 'Premium' : isStandard ? 'Standard' : 'Free'} Plan
        </h4>
        {shouldUseHook && (
          <button 
            className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center" 
            onClick={handleManualRefresh}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Refresh
          </button>
        )}
      </div>

      <div className="p-4">
        {/* Usage numbers prominently displayed */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-center px-4 py-2 bg-gray-50 rounded-lg flex-1 mr-2">
            <div className="text-2xl font-bold text-gray-800">{usedCount}</div>
            <div className="text-xs text-gray-500">Used</div>
          </div>
          <div className="text-center px-4 py-2 bg-gray-50 rounded-lg flex-1 mr-2">
            <div className="text-2xl font-bold text-gray-800">{remaining}</div>
            <div className="text-xs text-gray-500">Remaining</div>
          </div>
          <div className="text-center px-4 py-2 bg-gray-50 rounded-lg flex-1">
            <div className="text-2xl font-bold text-gray-800">{limit}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs font-medium text-gray-600">Usage ({percentage.toFixed(1)}%)</span>
            <span className={`text-xs font-medium ${
              usageLevel === 'critical' ? 'text-red-600' :
              usageLevel === 'warning' ? 'text-amber-600' :
              usageLevel === 'moderate' ? 'text-blue-600' : 'text-green-600'
            }`}> 
              {usageLevel === 'critical' ? 'Critical' :
               usageLevel === 'warning' ? 'High' :
               usageLevel === 'moderate' ? 'Moderate' : 'Low'}
            </span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${
                usageLevel === 'critical' ? 'bg-red-500' :
                usageLevel === 'warning' ? 'bg-amber-500' :
                usageLevel === 'moderate' ? 'bg-blue-500' : 'bg-green-500'
              }`}
              style={{ 
                width: `${percentage}%`,
                transition: 'width 0.5s ease-in-out'
              }}
            ></div>
          </div>
        </div>

        {/* Renewal Information */}
        <div className="text-xs text-gray-500 mb-4 bg-gray-50 rounded-md p-2 flex justify-between items-center">
          <span>Renews on {formattedRenewalDate}</span>
          <span className="font-medium">{daysUntilRenewal} days left</span>
        </div>

        {/* Usage Rate Indicator */}
        {limit > 0 && (
            <div className="text-xs bg-blue-50 rounded-md p-2 mb-4">
                <div className="flex justify-between items-center mb-1">
                    <span className="text-blue-700 font-medium">Estimated Usage</span>
                    <span className="text-blue-700">
                    {Math.round(usedCount / (today.getDate() / 30))} / month
                    </span>
                </div>
                <div className="w-full h-1.5 bg-blue-100 rounded-full overflow-hidden">
                    <div 
                    className="h-full bg-blue-400 rounded-full"
                    style={{ 
                        width: `${Math.min(100, (usedCount / Math.max(1, today.getDate()) * 30) / limit * 100)}%` 
                    }}
                    ></div>
                </div>
                <div className="mt-1 text-blue-600">
                    {usedCount / Math.max(1, today.getDate()) * 30 > limit ? 
                    "You're on track to exceed your monthly limit" : 
                    "You're within your monthly limit"}
                </div>
            </div>
        )}

        {/* Status Message */}
        {!canPerformAnalysis && (
          <div className={`p-3 rounded-md text-sm ${
            !hasActiveSubscription ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
          }`}>
            {!hasActiveSubscription ? (
              <p className="font-medium">Your subscription is inactive. <a href="/pricing" className="underline hover:text-red-800">Upgrade now</a></p>
            ) : (
              <p className="font-medium">You've reached your usage limit. <a href="/pricing" className="underline hover:text-amber-800">Upgrade your plan</a> for more analyses.</p>
            )}
          </div>
        )}

        {/* Detailed Plan Information - Only shown if detailed view */}
        {showDetailed && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Plan Details</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">Plan Type:</span>
                <span className={`font-medium ${
                  isPremium ? 'text-purple-600' : 
                  isStandard ? 'text-blue-600' : 
                  'text-gray-600'
                }`}>
                  {planType.charAt(0).toUpperCase() + planType.slice(1)}
                </span>
              </div>
              <div className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">Monthly Limit:</span>
                <span className="font-medium text-gray-700">{limit} analyses</span>
              </div>
              <div className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">Usage This Month:</span>
                <span className="font-medium text-gray-700">{usedCount} analyses ({(percentage).toFixed(1)}%)</span>
              </div>
              <div className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">Daily Average:</span>
                <span className="font-medium text-gray-700">
                  {limit > 0 ? (usedCount / Math.max(1, today.getDate())).toFixed(1) : 'N/A'} analyses/day
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status:</span>
                <span className={`font-medium ${hasActiveSubscription ? 'text-green-600' : 'text-red-600'}`}>
                  {hasActiveSubscription ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageIndicator; 